import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import {
  createMedicineOrderClassic,
  createMedicineOrderV2,
  createOrderPoRefillRequest,
  enrichOrderPoLines,
  fetchOrderMedicineCache,
  fetchOrderSubscriberCache,
  fetchOrderPoMedicationStock,
  importOrderPoDocument,
  learnOrderPoLineMapping,
  searchOrderPoMedications,
  searchOrderMedicines,
  searchOrderSubscribers,
  uploadOrderPrescriptionFiles,
  USE_ORDER_V2_CREATE,
} from "api/addOrderBeta/addOrderBetaApi";
import "./addOrderBeta.css";

const today = new Date().toISOString().slice(0, 10);
const SEARCH_CACHE_TTL = 1000 * 60 * 45;
const MEDICINE_CACHE_KEY = "medicineOrderBetaMedicineCacheV1";
const SUBSCRIBER_CACHE_KEY = "medicineOrderBetaSubscriberCacheV1";

const readTimedCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || "{}");
    if (
      cached &&
      cached.savedAt &&
      Date.now() - cached.savedAt < SEARCH_CACHE_TTL &&
      Array.isArray(cached.rows)
    ) {
      return cached.rows;
    }
  } catch (error) {}

  return [];
};

const writeTimedCache = (key, rows) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        savedAt: Date.now(),
        rows,
      }),
    );
  } catch (error) {}
};

const ORDER_TYPES = [
  { value: "1", label: "B2C", icon: "fa-user" },
  { value: "2", label: "B2B", icon: "fa-building" },
  { value: "3", label: "PickUp", icon: "fa-shopping-bag" },
  { value: "4", label: "Purchase Request", icon: "fa-clipboard" },
];

const SOURCE_OPTIONS = [
  { value: "1", label: "Flash" },
  { value: "2", label: "oPharma" },
  { value: "3", label: "MyPharma" },
  { value: "4", label: "Daraz" },
  { value: "5", label: "Pickme" },
  { value: "6", label: "E-channeling" },
  { value: "7", label: "Uber" },
  { value: "8", label: "Heart 2 Heart" },
  { value: "9", label: "Vida" },
  { value: "10", label: "PetDoc" },
  { value: "11", label: "LankaRemit" },
  { value: "12", label: "ComBankFlash" },
  { value: "13", label: "LiweCommunities" },
  { value: "14", label: "Synergyit" },
  { value: "15", label: "Delta" },
  { value: "17", label: "DirectPay" },
];

const DELIVERY_OPTIONS = [
  { value: "1", label: "Normal" },
  { value: "2", label: "Express" },
];

const PAYMENT_OPTIONS = [
  "Cash",
  "Card",
  "Cheque",
  "Bank Transfer",
  "Hybrid",
  "CeyPay",
  "QChatPay",
  "Swap Machine",
  "Mintpay",
];

const emptyLine = () => ({
  localId: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: "",
  batchNo: "",
  barcode: "",
  itemCode: "",
  title: "",
  quantity: 1,
  unitPrice: "",
  sellingPrice: "",
  availableQuantity: "",
  storeQuantity: "",
  pharmacyQuantity: "",
  allocatedQuantity: "",
  location: "",
  claimable: false,
  printable: false,
  upseller: false,
  partialSent: false,
  packed: false,
  note: "",
});

const initialForm = {
  orderType: "1",
  orderDate: today,
  source: "1",
  opharmaReference: "",
  mypharmaReference: "",
  flashReference: "Watsapp",
  subscriber: null,
  salesRep: "",
  deliveryType: "1",
  deliveryCharge: 0,
  deliveryFromDate: "",
  deliveryToDate: "",
  paymentOption: "Cash",
  paymentDueDate: today,
  healthCondition: "",
  isScheduleOrder: false,
  scheduleOrderDateTime: "",
  isCourier: false,
  rider: "",
  courierPartner: "",
  trackingId: "",
  trackingUrl: "",
  trackingNote: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  countryId: "1",
  addressId: "",
  billingAddressId: "",
  postalCode: "",
  orderNote: "",
  internalNote: "",
  poNumber: "",
  poValue: "",
  grnFiles: [],
  poFiles: [],
  prescriptionFiles: [],
  documentFiles: [],
  marginType: "1",
  marginValue: 0,
  discountPercentage: 0,
  isPartialOrder: false,
  isAbusableOrder: false,
  isClaimableOrder: false,
  lines: [emptyLine()],
};

const createInitialForm = () => ({
  ...initialForm,
  grnFiles: [],
  poFiles: [],
  prescriptionFiles: [],
  documentFiles: [],
  lines: [emptyLine()],
});

const getAuthContext = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    token: user.token || "",
    userId: user.id,
    store: user.store_id,
    role: user.role,
  };
};

const getSubscriberAddress = (item) => {
  const address =
    item.default_address ||
    item.address ||
    (Array.isArray(item.addresses) && item.addresses[0]) ||
    (Array.isArray(item.lead_addresses) && item.lead_addresses[0]) ||
    {};

  return {
    addressLine1:
      address.address_line1 ||
      address.address1 ||
      address.address ||
      item.address_line1 ||
      "",
    addressLine2:
      address.address_line2 || address.address2 || item.address_line2 || "",
    city:
      address.city_name ||
      address.city ||
      address.district_name ||
      item.city_name ||
      item.city ||
      "",
    postalCode: address.postal_code || item.postal_code || "",
  };
};

const cleanDisplayValue = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "none" || text.toLowerCase() === "nan") {
    return fallback;
  }
  return value;
};

const toSafeNumber = (value, fallback = 0) => {
  const cleaned = cleanDisplayValue(value, "");
  if (cleaned === "") return fallback;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : fallback;
};

const normalizeSearchText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");

const matchesSearchText = (query, values) => {
  const normalizedQuery = normalizeSearchText(query);
  const queryDigits = normalizeDigits(query);

  if (!normalizedQuery && !queryDigits) return false;

  return values.some((value) => {
    const normalizedValue = normalizeSearchText(value);
    const valueDigits = normalizeDigits(value);

    return (
      (normalizedQuery && normalizedValue.includes(normalizedQuery)) ||
      (queryDigits.length >= 3 &&
        valueDigits &&
        (valueDigits.includes(queryDigits) || valueDigits.endsWith(queryDigits)))
    );
  });
};

const getMedicineSearchValues = (medicine) => [
  medicine.title,
  medicine.name,
  medicine.generic_name,
  medicine.medication_name,
  medicine.item_code,
  medicine.code,
  medicine.itemCode,
  medicine.barcode,
];

const getSubscriberSearchValues = (subscriber) => [
  subscriber.name,
  subscriber.lead_name,
  subscriber.full_name,
  subscriber.mobile,
  subscriber.mobile_no,
  subscriber.phone,
  subscriber.email,
  subscriber.companyName,
  subscriber.company_name,
  subscriber.b2b && subscriber.b2b.company_name,
];

const localSearch = (rows, query, getValues, limit = 20) =>
  rows.filter((row) => matchesSearchText(query, getValues(row))).slice(0, limit);

const mapSubscriber = (item) => ({
  id: item.id,
  name: cleanDisplayValue(item.name || item.lead_name || item.full_name, ""),
  mobile: item.mobile_no || item.mobile || item.phone || "",
  email: item.email || "",
  b2bId:
    item.organization_id ||
    item.b2b_id ||
    (item.b2b && item.b2b.id) ||
    (item.b2b_organization && item.b2b_organization.id) ||
    "",
  b2bName:
    (item.b2b && item.b2b.company_name) ||
    (item.b2b_organization && item.b2b_organization.company_name) ||
    item.company_name ||
    item.subscriber_name ||
    "",
  address: getSubscriberAddress(item),
});

const looksLikeItemCode = (value) => /^F-\d+/i.test(String(value || "").trim());

const getMappedItemCode = (item) =>
  cleanDisplayValue(
    item.item_code ||
      item.code ||
      item.medication_itemcode ||
      item.itemCode ||
      (looksLikeItemCode(item.barcode) ? item.barcode : ""),
    "",
  );

const getMappedBarcode = (item) =>
  cleanDisplayValue(looksLikeItemCode(item.barcode) ? "" : item.barcode, "");

const getStoreWiseMedicationRows = (item) => {
  const rows =
    item.store_wise_medication ||
    item.store_wise_medication_multiple ||
    item.storeWiseMedication ||
    [];

  return Array.isArray(rows) ? rows : [];
};

const getStockRowName = (row) =>
  String(
    (row.pharmacies && row.pharmacies.name) ||
      (row.pharmacy && row.pharmacy.name) ||
      row.pharmacy_name ||
      row.store_name ||
      "",
  ).toLowerCase();

const getStockRowType = (row) =>
  String(
    (row.pharmacies && row.pharmacies.type) ||
      (row.pharmacy && row.pharmacy.type) ||
      row.store_type ||
      row.type ||
      "",
  ).toLowerCase();

const getStockRowQuantity = (row) =>
  toSafeNumber(
    row &&
      (row.quantity ||
        row.available_quantity ||
        row.total_quantity ||
        row.qty),
    0,
  );

const getStockRowLocation = (row) =>
  cleanDisplayValue(
    row &&
      (row.location ||
        row.rack_location ||
        row.bin_location ||
        (row.ycoord && row.xcoord ? `${row.ycoord}${row.xcoord}` : "")),
    "",
  );

const findStockRow = (rows, targetName, targetType) => {
  const normalizedTargetName = targetName.toLowerCase();
  const byName = rows.find((row) => getStockRowName(row) === normalizedTargetName);
  if (byName) return byName;

  return rows.find((row) => {
    const name = getStockRowName(row);
    const type = getStockRowType(row);

    return (
      type === targetType ||
      name.includes(targetType) ||
      (targetType === "warehouse" && name.includes("store"))
    );
  });
};

const getMedicationPlacement = (item, preferredRow) =>
  cleanDisplayValue(
    item.location ||
      item.rack_location ||
      item.bin_location ||
      (item.ycoord && item.xcoord ? `${item.ycoord}${item.xcoord}` : "") ||
      getStockRowLocation(preferredRow),
    "",
  );

const getMedicationInventory = (item) => {
  const rows = getStoreWiseMedicationRows(item);
  const warehouseRow = findStockRow(rows, "Flash WareHouse", "warehouse");
  const pharmacyRow = findStockRow(rows, "Flash Health Pharmacy", "pharmacy");
  const rowTotal = rows.reduce((sum, row) => sum + getStockRowQuantity(row), 0);
  const warehouseQuantity = getStockRowQuantity(warehouseRow);
  const pharmacyQuantity = getStockRowQuantity(pharmacyRow);

  return {
    availableQuantity: cleanDisplayValue(
      item.available_quantity ||
        item.total_quantity ||
        item.quantity ||
        item.stockQuantity ||
        item.totalAvailableQuantity ||
        rowTotal,
      0,
    ),
    storeQuantity: cleanDisplayValue(
      item.store_quantity ||
        item.storeQuantity ||
        item.storeLevelQuantity ||
        warehouseQuantity,
      0,
    ),
    pharmacyQuantity: cleanDisplayValue(
      item.pharmacy_quantity ||
        item.pharmacyQuantity ||
        item.pharmacy_stock ||
        item.pharmacyLevelQuantity ||
        pharmacyQuantity,
      0,
    ),
    allocatedQuantity: cleanDisplayValue(
      item.total_allocated ||
        item.allocated_quantity ||
        item.allocatedQuantity ||
        (pharmacyRow &&
          (pharmacyRow.total_allocated || pharmacyRow.allocated_quantity)) ||
        (warehouseRow &&
          (warehouseRow.total_allocated || warehouseRow.allocated_quantity)),
      0,
    ),
    location: getMedicationPlacement(item, pharmacyRow || warehouseRow),
  };
};

const mapMedicine = (item) => ({
  ...item,
  id: item.id,
  title: cleanDisplayValue(item.title || item.name || item.generic_name, ""),
  price: toSafeNumber(item.price || item.unit_price || item.selling_price, 0),
  sellingPrice: toSafeNumber(item.selling_price || item.price || item.unit_price, 0),
  ...getMedicationInventory(item),
  barcode: getMappedBarcode(item),
  itemCode: getMappedItemCode(item),
});

const formatMoney = (value) =>
  toSafeNumber(value, 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getLineTotal = (line) =>
  toSafeNumber(line.quantity, 0) * toSafeNumber(line.unitPrice, 0);

const parseMoneyValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : "";
};

const getApiRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getPoImportData = (response) => (response && response.data ? response.data : {});

const getImportMethodLabel = (method) => {
  const normalized = String(method || "").toLowerCase();
  if (normalized.includes("ai")) return "AI assisted reading";
  if (normalized === "pdf_text") return "PDF text reading";
  if (normalized === "pdf_ocr") return "PDF OCR reading";
  if (normalized === "image_ocr") return "image OCR reading";
  return "document reading";
};

const getMedicationTitle = (medication) =>
  medication
    ? medication.title || medication.name || medication.generic_name || "Selected medicine"
    : "";

const getMedicationPrice = (medication) =>
  medication
    ? medication.price ||
      medication.unit_price ||
      medication.selling_price ||
      (medication.pricing_summary && medication.pricing_summary.suggested_unit_price) ||
      ""
    : "";

const getMedicationItemCode = (medication) =>
  medication
    ? medication.item_code ||
      medication.code ||
      medication.medication_itemcode ||
      medication.itemCode ||
      ""
    : "";

const getMedicationStockQuantity = (medication) =>
  Number(
    medication && medication.stockQuantity !== undefined
      ? medication.stockQuantity
      : medication && medication.available_quantity !== undefined
        ? medication.available_quantity
        : medication && medication.total_quantity !== undefined
          ? medication.total_quantity
          : medication && medication.quantity !== undefined
            ? medication.quantity
            : 0,
  );

const getMedicationLineDetails = (medication, stock = {}) => ({
  barcode: getMappedBarcode({
    barcode: medication.barcode || stock.barcode || "",
  }),
  itemCode:
    medication.item_code ||
    medication.code ||
    medication.medication_itemcode ||
    medication.itemCode ||
    stock.item_code ||
    stock.medication_itemcode ||
    (looksLikeItemCode(medication.barcode) ? medication.barcode : "") ||
    "",
  sellingPrice:
    medication.sellingPrice ||
    medication.selling_price ||
    medication.price ||
    medication.unit_price ||
    stock.selling_price ||
    "",
  availableQuantity:
    stock.availableQuantity !== undefined && stock.availableQuantity !== ""
      ? stock.availableQuantity
      : getMedicationStockQuantity(medication),
  storeQuantity:
    stock.storeQuantity !== undefined && stock.storeQuantity !== ""
      ? stock.storeQuantity
      : medication.store_quantity || medication.storeQuantity || "",
  pharmacyQuantity:
    stock.pharmacyQuantity !== undefined && stock.pharmacyQuantity !== ""
      ? stock.pharmacyQuantity
      : medication.pharmacy_quantity || medication.pharmacyQuantity || medication.pharmacy_stock || "",
  allocatedQuantity:
    stock.allocatedQuantity !== undefined && stock.allocatedQuantity !== ""
      ? stock.allocatedQuantity
      : medication.total_allocated || medication.allocated_quantity || medication.allocatedQuantity || "",
  location:
    stock.location ||
    medication.location ||
    medication.rack_location ||
    medication.bin_location ||
    "",
});

const getShortageQuantity = (line) =>
  Math.max(0, Number(line.quantity || 0) - Number(line.availableQuantity || 0));

const getPoUnitPrice = (line) => {
  const unitPrice = parseMoneyValue(line.requestedPrice);
  if (unitPrice !== "") return unitPrice;

  const totalPrice = parseMoneyValue(line.requestedTotal);
  const quantity = Number(line.quantity || 0);
  if (totalPrice !== "" && quantity > 0) {
    return totalPrice / quantity;
  }

  return "";
};

const getPoLineTotal = (line) => {
  const explicitTotal = parseMoneyValue(line.requestedTotal);
  if (explicitTotal !== "") return explicitTotal;

  const unitPrice = getPoUnitPrice(line);
  return unitPrice === "" ? 0 : Number(line.quantity || 0) * unitPrice;
};

const getSystemLineTotal = (line) =>
  line.medication
    ? Number(line.quantity || 0) * Number(getMedicationPrice(line.medication) || 0)
    : 0;

const formatDateForApi = (value) => {
  if (!value) return today;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const getUploadedPrescriptionUrl = (payload) => {
  if (!payload) return "";
  return (
    (payload.data && payload.data.prescription_url) ||
    payload.prescription_url ||
    ""
  );
};

const normalizePoLookupText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractPoPriceRowsFromText = (rawText) => {
  const rows = [];
  const lines = String(rawText || "").split(/\r\n|\r|\n/);

  lines.forEach((rawLine) => {
    const line = rawLine.replace(/\s+/g, " ").trim();
    if (!line || !/\d{1,2}[-/][A-Za-z]{3}[-/]\d{4}/i.test(line)) return;

    const priceMatch = line.match(
      /\b\d{1,2}[-/][A-Za-z]{3}[-/]\d{4}\b\s+([\d,]+\.\d{2})\s+([\d,]+\.\d{2})/,
    );
    if (!priceMatch) return;

    const beforeDate = line.slice(0, priceMatch.index).replace(/[|[\]}]+/g, " ").trim();
    const codeMatch = beforeDate.match(/\b[A-Z]{1,4}-[A-Z]{1,4}-\d{1,4}\b/i);
    const nameSource = codeMatch
      ? beforeDate.slice(codeMatch.index + codeMatch[0].length)
      : beforeDate;
    const requestedName = nameSource
      .replace(/^\s*\d+\s+/, "")
      .replace(/^\s*\d+(?:,\d{3})*(?:\.\d+)?\s+/, "")
      .replace(/\s+/g, " ")
      .trim();

    if (!requestedName || normalizePoLookupText(requestedName).length < 3) return;

    rows.push({
      requestedName,
      requestedPrice: parseMoneyValue(priceMatch[1]),
      requestedTotal: parseMoneyValue(priceMatch[2]),
    });
  });

  return rows;
};

const mergeImportedPoPrices = (items, rawText) => {
  const priceRows = extractPoPriceRowsFromText(rawText);
  if (!priceRows.length) return items;

  return items.map((item) => {
    if (
      parseMoneyValue(
        item.unit_price ||
          item.requested_unit_price ||
          item.quoted_unit_price ||
          item.po_unit_price ||
          item.price ||
          item.rate,
      ) !== "" ||
      parseMoneyValue(
        item.total_price ||
          item.requested_total ||
          item.line_total ||
          item.total ||
          item.amount,
      ) !== ""
    ) {
      return item;
    }

    const itemName = normalizePoLookupText(
      item.requested_name || item.name || item.item || "",
    );
    const matchedPriceRow = priceRows.find((row) => {
      const rowName = normalizePoLookupText(row.requestedName);
      return rowName && itemName && (rowName.includes(itemName) || itemName.includes(rowName));
    });

    if (!matchedPriceRow) return item;

    return {
      ...item,
      unit_price: matchedPriceRow.requestedPrice,
      line_total: matchedPriceRow.requestedTotal,
    };
  });
};

const isMissingOcrToolError = (error) => {
  const message =
    (error &&
      error.response &&
      error.response.data &&
      error.response.data.message) ||
    error.message ||
    "";
  return /ocr needs|tesseract|ghostscript/i.test(message);
};

const Field = ({ label, children, full }) => (
  <label className={`aob-field ${full ? "aob-field-full" : ""}`}>
    <span>{label}</span>
    {children}
  </label>
);

const FileSummary = ({ files }) => {
  if (!files.length) {
    return <span className="aob-file-empty">No file selected</span>;
  }

  return (
    <span className="aob-file-count">
      {files.length} file{files.length > 1 ? "s" : ""} ready
    </span>
  );
};

const AddOrderBeta = ({ history }) => {
  const [form, setForm] = useState(createInitialForm);
  const [subscriberQuery, setSubscriberQuery] = useState("");
  const [subscriberResults, setSubscriberResults] = useState([]);
  const [activeMedicineLineId, setActiveMedicineLineId] = useState("");
  const [lineMedicineResults, setLineMedicineResults] = useState([]);
  const [lineMedicineHighlightIndex, setLineMedicineHighlightIndex] = useState(0);
  const [loadingKey, setLoadingKey] = useState("");
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [poMessage, setPoMessage] = useState(null);
  const [poReviewOpen, setPoReviewOpen] = useState(false);
  const [poReviewLines, setPoReviewLines] = useState([]);
  const [poImportSummary, setPoImportSummary] = useState(null);
  const [poMedicineSearchIndex, setPoMedicineSearchIndex] = useState(null);
  const [poMedicineSearchQuery, setPoMedicineSearchQuery] = useState("");
  const [poMedicineSearchResults, setPoMedicineSearchResults] = useState([]);
  const [poMedicineSearchLoading, setPoMedicineSearchLoading] = useState(false);
  const [poAppliedOnce, setPoAppliedOnce] = useState(false);
  const [poRefillCreated, setPoRefillCreated] = useState(false);
  const [poIntakeOpen, setPoIntakeOpen] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [itemFilter, setItemFilter] = useState("all");
  const [medicineCache, setMedicineCache] = useState(() =>
    readTimedCache(MEDICINE_CACHE_KEY),
  );
  const [subscriberCache, setSubscriberCache] = useState(() =>
    readTimedCache(SUBSCRIBER_CACHE_KEY),
  );
  const [searchCacheLoading, setSearchCacheLoading] = useState({
    medicines: false,
    subscribers: false,
  });
  const lineMedicineInputRefs = useRef({});
  const lineQuantityInputRefs = useRef({});

  const auth = useMemo(() => getAuthContext(), []);

  const total = useMemo(
    () => form.lines.reduce((sum, line) => sum + getLineTotal(line), 0),
    [form.lines],
  );
  const isPickup = form.orderType === "3";
  const isPurchaseRequest = form.orderType === "4";
  const isB2b = form.orderType === "2";
  const needsDeliveryFields = !isPickup && !isPurchaseRequest;
  const needsPaymentFields = !isPurchaseRequest;
  const normalizedItemSearch = itemSearch.trim().toLowerCase();
  const itemRows = useMemo(
    () =>
      form.lines.map((line, index) => {
        const availableQuantity = toSafeNumber(line.availableQuantity, 0);
        const quantity = toSafeNumber(line.quantity, 0);
        const isAvailable = !line.partialSent;
        const isLowStock =
          line.id && (availableQuantity <= 0 || quantity > availableQuantity);
        const searchText = [
          line.title,
          line.itemCode,
          line.barcode,
          line.batchNo,
          line.location,
          line.quantity,
          line.unitPrice,
        ]
          .join(" ")
          .toLowerCase();

        return {
          line,
          index,
          availableQuantity,
          quantity,
          isAvailable,
          isLowStock,
          searchText,
        };
      }),
    [form.lines],
  );
  const visibleItemRows = itemRows.filter((row) => {
    const matchesSearch =
      !normalizedItemSearch || row.searchText.includes(normalizedItemSearch);

    if (!matchesSearch) return false;
    if (itemFilter === "unpacked") return !row.line.packed;
    if (itemFilter === "shortage") return row.isLowStock;
    if (itemFilter === "unmatched") return !row.line.id;
    return true;
  });
  const itemFilterCounts = {
    all: itemRows.length,
    unpacked: itemRows.filter((row) => !row.line.packed).length,
    shortage: itemRows.filter((row) => row.isLowStock).length,
    unmatched: itemRows.filter((row) => !row.line.id).length,
  };

  useEffect(() => {
    if (!auth.token) return;

    let cancelled = false;

    setSearchCacheLoading((current) => ({ ...current, medicines: true }));
    fetchOrderMedicineCache(auth.token, "1")
      .then((rows) => {
        if (cancelled) return;
        const mappedRows = rows.map(mapMedicine);
        setMedicineCache(mappedRows);
        writeTimedCache(MEDICINE_CACHE_KEY, mappedRows);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setSearchCacheLoading((current) => ({ ...current, medicines: false }));
        }
      });

    setSearchCacheLoading((current) => ({ ...current, subscribers: true }));
    fetchOrderSubscriberCache(auth.token)
      .then((rows) => {
        if (cancelled) return;
        const mappedRows = rows.map(mapSubscriber);
        setSubscriberCache(mappedRows);
        writeTimedCache(SUBSCRIBER_CACHE_KEY, mappedRows);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setSearchCacheLoading((current) => ({
            ...current,
            subscribers: false,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth.token]);

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
    setErrors((current) => ({
      ...current,
      [key]: "",
    }));
  };

  const updateLine = (localId, key, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) =>
        line.localId === localId ? { ...line, [key]: value } : line,
      ),
    }));
  };

  const addLine = (focusMedicine = false) => {
    const shouldFocusMedicine = focusMedicine === true;
    const nextLine = emptyLine();
    setForm((current) => ({
      ...current,
      lines: [...current.lines, nextLine],
    }));
    if (shouldFocusMedicine) {
      setTimeout(() => {
        if (lineMedicineInputRefs.current[nextLine.localId]) {
          lineMedicineInputRefs.current[nextLine.localId].focus();
        }
      }, 0);
    }
  };

  const addLineRows = (count = 1) => {
    setForm((current) => ({
      ...current,
      lines: [
        ...current.lines,
        ...Array.from({ length: Math.max(1, Number(count) || 1) }, emptyLine),
      ],
    }));
  };

  const handleOrderTypeChange = (value) => {
    updateField("orderType", value);
    if (value === "2") {
      setPoIntakeOpen(true);
    }
  };

  const removeLine = (localId) => {
    setForm((current) => ({
      ...current,
      lines:
        current.lines.length === 1
          ? [emptyLine()]
          : current.lines.filter((line) => line.localId !== localId),
    }));
  };

  useEffect(() => {
    const query = subscriberQuery.trim();

    if (!query) {
      setSubscriberResults([]);
      return;
    }

    if (form.subscriber && query === `${form.subscriber.name} ${form.subscriber.mobile || ""}`.trim()) {
      return;
    }

    if (query.length < 3) {
      return;
    }

    const localMatches = localSearch(
      subscriberCache,
      query,
      getSubscriberSearchValues,
      20,
    );
    setSubscriberResults(localMatches);

    const queryDigits = query.replace(/\D/g, "");
    const exactLocalPhoneMatch =
      queryDigits.length >= 7
        ? localMatches.find((subscriber) => {
            const mobileDigits = String(subscriber.mobile || "").replace(/\D/g, "");
            return mobileDigits === queryDigits || mobileDigits.endsWith(queryDigits);
          })
        : null;

    if (exactLocalPhoneMatch) {
      selectSubscriber(exactLocalPhoneMatch);
      return;
    }

    if (localMatches.length && query.length < 7 && queryDigits.length < 6) {
      return;
    }

    if (
      subscriberCache.length &&
      !localMatches.length &&
      query.length < 7 &&
      queryDigits.length < 6
    ) {
      return;
    }

    const timer = setTimeout(() => {
      setLoadingKey("subscribers");
      setMessage(null);
      searchOrderSubscribers(auth.token, query)
        .then((rows) => {
          const mapped = rows.map(mapSubscriber);
          const queryDigits = query.replace(/\D/g, "");
          const exactPhoneMatch =
            queryDigits.length >= 7
              ? mapped.find((subscriber) => {
                  const mobileDigits = String(subscriber.mobile || "").replace(
                    /\D/g,
                    "",
                  );

                  return (
                    mobileDigits === queryDigits ||
                    mobileDigits.endsWith(queryDigits)
                  );
                })
              : null;

          setSubscriberResults(mapped);

          if (exactPhoneMatch) {
            selectSubscriber(exactPhoneMatch);
          }
        })
        .catch(() => {
          setMessage({
            type: "error",
            text: "Subscriber search failed. Please try again or use the classic page.",
          });
        })
        .finally(() => setLoadingKey(""));
    }, 450);

    return () => clearTimeout(timer);
  }, [auth.token, form.subscriber, subscriberCache, subscriberQuery]);

  const selectSubscriber = (subscriber, clearResults = true) => {
    setForm((current) => ({
      ...current,
      subscriber,
      addressLine1:
        subscriber.address && subscriber.address.addressLine1
          ? subscriber.address.addressLine1
          : current.addressLine1,
      addressLine2:
        subscriber.address && subscriber.address.addressLine2
          ? subscriber.address.addressLine2
          : current.addressLine2,
      city:
        subscriber.address && subscriber.address.city
          ? subscriber.address.city
          : current.city,
      postalCode:
        subscriber.address && subscriber.address.postalCode
          ? subscriber.address.postalCode
          : current.postalCode,
    }));
    if (
      form.orderType === "2" &&
      form.subscriber &&
      form.subscriber.b2bId &&
      subscriber.b2bId &&
      String(form.subscriber.b2bId) !== String(subscriber.b2bId)
    ) {
      setPoReviewLines([]);
      setPoImportSummary(null);
      setPoReviewOpen(false);
      setPoAppliedOnce(false);
      setPoRefillCreated(false);
      setPoMessage({
        type: "info",
        text: "B2B company changed. Please process this PO again for the selected company.",
      });
    }
    setErrors((current) => ({
      ...current,
      subscriber: "",
    }));
    if (clearResults) {
      setSubscriberResults([]);
    }
    setSubscriberQuery(`${subscriber.name} ${subscriber.mobile || ""}`.trim());
  };

  useEffect(() => {
    if (!activeMedicineLineId) {
      setLineMedicineResults([]);
      return;
    }

    const activeLine = form.lines.find(
      (line) => line.localId === activeMedicineLineId,
    );
    const query = activeLine && activeLine.title ? activeLine.title.trim() : "";

    if (!query || query.length < 3) {
      setLineMedicineResults([]);
      return;
    }

    const localMatches = localSearch(
      medicineCache,
      query,
      getMedicineSearchValues,
      20,
    );
    setLineMedicineResults(localMatches);

    if (localMatches.length && query.length < 7) {
      return;
    }

    if (medicineCache.length && !localMatches.length && query.length < 7) {
      return;
    }

    const timer = setTimeout(() => {
      setLoadingKey("medicines");
      setMessage(null);
      searchOrderMedicines(auth.token, query, "1", auth.store)
        .then((rows) => setLineMedicineResults(rows.map(mapMedicine)))
        .catch(() => {
          setMessage({
            type: "error",
            text: "Medicine search failed. You can still use this line as a manual item.",
          });
        })
        .finally(() => setLoadingKey(""));
    }, 450);

    return () => clearTimeout(timer);
  }, [activeMedicineLineId, auth.store, auth.token, form.lines, medicineCache]);

  useEffect(() => {
    setLineMedicineHighlightIndex(0);
  }, [activeMedicineLineId, lineMedicineResults]);

  const selectLineMedicine = (localId, medicine, focusQuantity = false) => {
    const medicineDetails = getMedicationLineDetails(medicine);

    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) =>
        line.localId === localId
          ? {
              ...line,
              id: medicine.id,
              title: medicine.title,
              unitPrice: medicine.price,
              barcode: medicineDetails.barcode,
              itemCode: medicineDetails.itemCode,
              sellingPrice: medicineDetails.sellingPrice,
              availableQuantity: medicineDetails.availableQuantity,
              storeQuantity: medicineDetails.storeQuantity,
              pharmacyQuantity: medicineDetails.pharmacyQuantity,
              allocatedQuantity: medicineDetails.allocatedQuantity,
              location: medicineDetails.location,
            }
          : line,
      ),
    }));
    setLineMedicineResults([]);
    setActiveMedicineLineId("");
    if (focusQuantity) {
      setTimeout(() => {
        if (lineQuantityInputRefs.current[localId]) {
          lineQuantityInputRefs.current[localId].focus();
          lineQuantityInputRefs.current[localId].select();
        }
      }, 0);
    }
  };

  const handleLineMedicineKeyDown = (event, localId) => {
    if (activeMedicineLineId !== localId) {
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      return;
    }

    event.preventDefault();

    if (!lineMedicineResults.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      setLineMedicineHighlightIndex((current) =>
        Math.min(current + 1, lineMedicineResults.length - 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      setLineMedicineHighlightIndex((current) => Math.max(current - 1, 0));
      return;
    }

    const selectedMedicine =
      lineMedicineResults[
        Math.min(lineMedicineHighlightIndex, lineMedicineResults.length - 1)
      ];
    if (selectedMedicine) {
      selectLineMedicine(localId, selectedMedicine, true);
    }
  };

  const handleLineQuantityKeyDown = (event, line, rowIndex) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    const nextRow = visibleItemRows[rowIndex + 1];

    if (nextRow && lineMedicineInputRefs.current[nextRow.line.localId]) {
      lineMedicineInputRefs.current[nextRow.line.localId].focus();
      return;
    }

    if (line.title || line.id) {
      addLine(true);
    }
  };

  const getSubscriberB2bId = () =>
    form.subscriber && form.subscriber.b2bId ? form.subscriber.b2bId : null;

  const getSubscriberLeadId = () =>
    form.subscriber && form.subscriber.id ? form.subscriber.id : null;

  const validatePoB2bContext = () => {
    if (!form.subscriber || !getSubscriberB2bId()) {
      setPoMessage({
        type: "warning",
        text: "Select the B2B subscriber/company before processing or rechecking the PO.",
      });
      return false;
    }

    return true;
  };

  const mapImportedPoItem = (item) => ({
    requestedName: item.requested_name || item.name || "",
    quantity: item.requested_quantity || item.quantity || 1,
    requestedUnit: item.requested_unit || item.unit || "",
    requestedPrice:
      parseMoneyValue(
        item.unit_price ||
          item.requested_unit_price ||
          item.quoted_unit_price ||
          item.po_unit_price ||
          item.price ||
          item.rate,
      ) || "",
    requestedTotal:
      parseMoneyValue(
        item.total_price ||
          item.requested_total ||
          item.line_total ||
          item.total ||
          item.amount,
      ) || "",
    confidence: item.confidence || "",
    status: item.status || "",
    selected: true,
    medication: null,
    availableQuantity: 0,
    refillRequested: false,
    refillQuantity: "",
    refillRemark: "",
  });

  const hydratePoLineStock = async (line) => {
    if (!line.medication || !line.medication.id) {
      return {
        ...line,
        availableQuantity: 0,
        refillQuantity: line.refillQuantity || "",
      };
    }

    let stockDetails = getMedicationLineDetails(line.medication);
    let availableQuantity = Number(stockDetails.availableQuantity || 0);

    try {
      const stockRes = await fetchOrderPoMedicationStock(
        auth.token,
        line.medication.id,
        auth.store,
      );
      const stockRows = getApiRows(stockRes);
      if (stockRows.length) {
        availableQuantity = stockRows.reduce(
          (sum, row) => sum + Number(row.quantity || row.available_quantity || 0),
          0,
        );
        const pharmacyRow =
          stockRows.find((row) => Number(row.store_id || row.storeId) === Number(auth.store)) ||
          stockRows[0] ||
          {};
        stockDetails = {
          ...stockDetails,
          availableQuantity,
          storeQuantity: availableQuantity,
          pharmacyQuantity:
            pharmacyRow.quantity ||
            pharmacyRow.available_quantity ||
            pharmacyRow.pharmacy_quantity ||
            stockDetails.pharmacyQuantity,
          allocatedQuantity:
            pharmacyRow.total_allocated ||
            pharmacyRow.allocated_quantity ||
            stockDetails.allocatedQuantity,
          location:
            pharmacyRow.location ||
            pharmacyRow.rack_location ||
            pharmacyRow.bin_location ||
            stockDetails.location,
        };
      } else if (stockRes && stockRes.data && stockRes.data.medication) {
        stockDetails = getMedicationLineDetails(stockRes.data.medication);
        availableQuantity = Number(stockDetails.availableQuantity || 0);
      }
    } catch (error) {
      stockDetails = getMedicationLineDetails(line.medication);
      availableQuantity = Number(stockDetails.availableQuantity || 0);
    }

    const shortageQuantity = Math.max(0, Number(line.quantity || 0) - availableQuantity);

    return {
      ...line,
      medication: {
        ...line.medication,
        stockQuantity: availableQuantity,
      },
      availableQuantity,
      barcode: stockDetails.barcode,
      itemCode: stockDetails.itemCode,
      sellingPrice: stockDetails.sellingPrice,
      storeQuantity: stockDetails.storeQuantity,
      pharmacyQuantity: stockDetails.pharmacyQuantity,
      allocatedQuantity: stockDetails.allocatedQuantity,
      location: stockDetails.location,
      refillRequested: shortageQuantity > 0 ? true : line.refillRequested,
      refillQuantity: shortageQuantity > 0 ? shortageQuantity : line.refillQuantity || "",
      refillRemark:
        shortageQuantity > 0 && !line.refillRemark
          ? `Auto refill from PO intake for ${line.requestedName}`
          : line.refillRemark,
    };
  };

  const processPoDocument = async (poFilesOverride = null) => {
    const filesToProcess = poFilesOverride || form.poFiles;
    setPoMessage(null);

    if (!isB2b) {
      setPoMessage({ type: "warning", text: "Switch the order type to B2B before processing a PO." });
      return;
    }

    if (!filesToProcess.length) {
      setPoMessage({ type: "warning", text: "Upload the PO document before processing." });
      return;
    }

    if (!validatePoB2bContext()) {
      return;
    }

    setLoadingKey("po");
    setPoMessage({
      type: "info",
      text: form.subscriber
        ? "Reading all PO items and matching Flash medicines with this B2B account..."
        : "Reading all PO items and matching Flash medicines...",
    });
    setMessage({ type: "info", text: "Reading all PO items and matching Flash medicines..." });

    try {
      let response;
      try {
        response = await importOrderPoDocument(
          auth.token,
          filesToProcess[0],
          getSubscriberB2bId(),
          getSubscriberLeadId(),
        );
      } catch (error) {
        if (!isMissingOcrToolError(error)) throw error;
        response = await importOrderPoDocument(
          auth.token,
          filesToProcess[0],
          getSubscriberB2bId(),
          getSubscriberLeadId(),
          { forceAi: true },
        );
      }

      const importData = getPoImportData(response);
      const importedItems = Array.isArray(importData.items)
        ? mergeImportedPoPrices(importData.items, importData.raw_text)
        : [];

      if (!importedItems.length) {
        setPoMessage({
          type: "warning",
          text: "No PO item rows were found. Try a clearer file or add the lines manually.",
        });
        return;
      }

      const reviewLines = importedItems.map(mapImportedPoItem);
      const enrichedResponse = await enrichOrderPoLines(
        auth.token,
        reviewLines.map((line) => ({
          requested_name: line.requestedName,
          requested_quantity: line.quantity,
          quoted_unit_price: line.requestedPrice || null,
        })),
        getSubscriberB2bId(),
        getSubscriberLeadId(),
      );
      const enrichedRows = getApiRows(enrichedResponse);

      const matchedLines = await Promise.all(
        reviewLines.map((line, index) =>
          hydratePoLineStock({
            ...line,
            medication: enrichedRows[index] && enrichedRows[index].medication
              ? enrichedRows[index].medication
              : null,
          }),
        ),
      );

      setPoReviewLines(matchedLines);
      setPoAppliedOnce(false);
      setPoRefillCreated(false);
      setPoImportSummary({
        fileName: filesToProcess[0].name,
        count: matchedLines.length,
        method: getImportMethodLabel(importData.extraction_method),
      });
      setPoReviewOpen(true);
      setPoIntakeOpen(false);
      setPoMessage({
        type: "success",
        text: `${matchedLines.length} PO line(s) are ready for review.`,
      });
      setMessage({
        type: "success",
        text: `${matchedLines.length} PO line(s) are ready for review.`,
      });
    } catch (error) {
      const errorMessage =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.message) ||
        "Could not process this PO. Please try another file or add the items manually.";
      setPoMessage({
        type: "error",
        text: errorMessage,
      });
      setMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      setLoadingKey("");
    }
  };

  const updatePoReviewLine = (index, key, value) => {
    setPoReviewLines((current) =>
      current.map((line, lineIndex) => {
        if (lineIndex !== index) return line;

        const updatedLine = { ...line, [key]: value };
        if (key === "quantity") {
          const shortageQuantity = getShortageQuantity(updatedLine);
          updatedLine.refillRequested = shortageQuantity > 0;
          updatedLine.refillQuantity = shortageQuantity > 0 ? shortageQuantity : "";
        }

        return updatedLine;
      }),
    );
  };

  useEffect(() => {
    if (poMedicineSearchIndex === null) {
      setPoMedicineSearchResults([]);
      return;
    }

    const query = poMedicineSearchQuery.trim();
    if (query.length < 3) {
      setPoMedicineSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      setPoMedicineSearchLoading(true);
      searchOrderPoMedications(
        auth.token,
        query,
        getSubscriberB2bId(),
        getSubscriberLeadId(),
      )
        .then((res) => setPoMedicineSearchResults(getApiRows(res)))
        .catch(() => setPoMedicineSearchResults([]))
        .finally(() => setPoMedicineSearchLoading(false));
    }, 350);

    return () => clearTimeout(timer);
  }, [auth.token, form.subscriber, poMedicineSearchIndex, poMedicineSearchQuery]);

  const openPoMedicineSearch = (index) => {
    const line = poReviewLines[index] || {};
    setPoMedicineSearchIndex(index);
    setPoMedicineSearchQuery(
      line.medication ? getMedicationTitle(line.medication) : line.requestedName || "",
    );
  };

  const rememberPoLineMedication = async (line, medication) => {
    const b2bId = getSubscriberB2bId();

    if (!form.subscriber || !b2bId || !line.requestedName || !medication || !medication.id) {
      setPoMessage({
        type: "warning",
        text: "Medicine selected. Learning was skipped because this subscriber is not linked to a B2B company.",
      });
      return;
    }

    try {
      await learnOrderPoLineMapping(auth.token, {
        b2b_id: b2bId,
        lead_id: getSubscriberLeadId(),
        requested_name: line.requestedName,
        medication_id: medication.id,
        metadata: {
          source: "medicine_order_beta_po_intake",
          po_unit_price: line.requestedPrice || null,
          po_unit: line.requestedUnit || null,
        },
      });
    } catch (error) {
      const learningError =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.message) ||
        "Medicine selected, but the B2B learning mapping could not be saved.";
      setPoMessage({
        type: "warning",
        text: learningError,
      });
    }
  };

  const selectPoMedication = async (index, medication) => {
    const line = poReviewLines[index];
    const hydratedLine = await hydratePoLineStock({
      ...line,
      medication,
    });
    setPoReviewLines((current) =>
      current.map((candidate, lineIndex) =>
        lineIndex === index ? hydratedLine : candidate,
      ),
    );
    setPoMedicineSearchIndex(null);
    setPoMedicineSearchResults([]);
    setPoMedicineSearchQuery("");
    await rememberPoLineMedication(line, medication);
  };

  const addPoReviewLine = () => {
    setPoReviewLines((current) => [
      ...current,
      {
        ...mapImportedPoItem({ requested_name: "" }),
        selected: true,
      },
    ]);
  };

  const recheckPoReviewLines = async () => {
    if (!poReviewLines.length) {
      setPoMessage({ type: "warning", text: "Process a PO before rechecking matches." });
      return;
    }

    if (!validatePoB2bContext()) {
      return;
    }

    setLoadingKey("po-recheck");
    setPoMessage({ type: "info", text: "Rechecking PO lines and looking for remaining medicine matches..." });

    try {
      const enrichedResponse = await enrichOrderPoLines(
        auth.token,
        poReviewLines.map((line) => ({
          requested_name: line.requestedName,
          requested_quantity: line.quantity,
          quoted_unit_price: line.requestedPrice || null,
        })),
        getSubscriberB2bId(),
        getSubscriberLeadId(),
      );
      const enrichedRows = getApiRows(enrichedResponse);
      const recheckedLines = await Promise.all(
        poReviewLines.map((line, index) =>
          hydratePoLineStock({
            ...line,
            medication:
              line.medication ||
              (enrichedRows[index] && enrichedRows[index].medication
                ? enrichedRows[index].medication
                : null),
          }),
        ),
      );

      setPoReviewLines(recheckedLines);
      setPoMessage({ type: "success", text: "PO matches were rechecked. Review and apply again when ready." });
    } catch (error) {
      setPoMessage({
        type: "error",
        text: "Could not recheck the PO matches. You can still edit the rows manually.",
      });
    } finally {
      setLoadingKey("");
    }
  };

  const createRefillRequestForShortages = async (shortageLines) => {
    const refillLines = shortageLines.filter(
      (line) =>
        line.refillRequested &&
        line.medication &&
        line.medication.id &&
        Number(line.refillQuantity || getShortageQuantity(line)) > 0,
    );

    if (!refillLines.length) return false;

    const payload = {
      reference: form.poNumber || "",
      request_date: new Date().toISOString(),
      status: 1,
      request_by: auth.userId,
      medications: refillLines.map((line) => ({
        id: line.medication.id,
        quantity: Number(line.refillQuantity || getShortageQuantity(line)),
        remarks: line.refillRemark || "",
      })),
      store_id: auth.store,
    };

    const response = await createOrderPoRefillRequest(auth.token, payload);
    return Boolean(
      response &&
        (response.success ||
          (response.data && response.data.success) ||
          response.status === true),
    );
  };

  const applyPoReviewLines = async () => {
    const selectedLines = poReviewLines.filter((line) => line.selected);
    const mappedSelectedLines = selectedLines.filter(
      (line) => line.medication && line.medication.id,
    );
    const unmappedSelectedCount = selectedLines.length - mappedSelectedLines.length;

    if (!selectedLines.length) {
      setMessage({ type: "warning", text: "Select at least one PO line to apply." });
      return;
    }

    if (!mappedSelectedLines.length) {
      setMessage({
        type: "warning",
        text: "Select at least one matched Flash medicine before applying.",
      });
      setPoMessage({
        type: "warning",
        text: "Select at least one matched Flash medicine before applying.",
      });
      return;
    }

    const importedLines = mappedSelectedLines
      .map((line) => {
        const medication = line.medication || {};
        const unitPrice =
          line.requestedPrice ||
          medication.price ||
          medication.unit_price ||
          medication.selling_price ||
          (medication.pricing_summary && medication.pricing_summary.suggested_unit_price) ||
          "";

        return {
          ...emptyLine(),
          id: medication.id || "",
          title: medication.title || medication.name || "",
          barcode: line.barcode || medication.barcode || "",
          itemCode: line.itemCode || medication.item_code || medication.code || "",
          quantity: line.quantity || 1,
          unitPrice,
          sellingPrice: line.sellingPrice || medication.selling_price || unitPrice,
          availableQuantity: line.availableQuantity,
          storeQuantity: line.storeQuantity,
          pharmacyQuantity: line.pharmacyQuantity,
          allocatedQuantity: line.allocatedQuantity,
          location: line.location,
          refillRequested: line.refillRequested,
          refillQuantity: line.refillQuantity,
          note: [
            line.requestedUnit ? `PO unit: ${line.requestedUnit}` : "",
            line.refillRequested ? `Refill requested: ${line.refillQuantity}` : "",
          ]
            .filter(Boolean)
            .join(" | "),
        };
      });

    const shortageLines = poReviewLines.filter(
      (line) =>
        line.selected &&
        line.medication &&
        line.medication.id &&
        getShortageQuantity(line) > 0,
    );

    setLoadingKey("po-apply");
    try {
      const refillCreated =
        poRefillCreated || !shortageLines.length
          ? false
          : await createRefillRequestForShortages(shortageLines);

      setForm((current) => ({
        ...current,
        lines: importedLines,
      }));
      setPoAppliedOnce(true);
      if (refillCreated) {
        setPoRefillCreated(true);
      }
      setPoReviewOpen(false);
      setMessage({
        type: "success",
        text: refillCreated
          ? `${importedLines.length} matched PO line(s) applied and refill request created.${unmappedSelectedCount ? ` ${unmappedSelectedCount} unmatched line(s) were skipped.` : ""}`
          : poRefillCreated
            ? `${importedLines.length} matched PO line(s) applied. The refill request was already created for this PO.${unmappedSelectedCount ? ` ${unmappedSelectedCount} unmatched line(s) were skipped.` : ""}`
          : `${importedLines.length} matched PO line(s) applied to this order.${unmappedSelectedCount ? ` ${unmappedSelectedCount} unmatched line(s) were skipped.` : ""}`,
      });
    } catch (error) {
      setPoMessage({
        type: "error",
        text: "Could not create the refill request. The PO lines were not applied yet.",
      });
    } finally {
      setLoadingKey("");
    }
  };

  const validate = () => {
    const nextErrors = {};
    const validLines = form.lines.filter(
      (line) => line.title && Number(line.quantity) > 0,
    );
    const canCreateOrderWithoutMedicine = Number(auth.role) === 19;

    if (!form.subscriber) {
      nextErrors.subscriber = "Select a subscriber before creating the order.";
    }

    if (!form.source) {
      nextErrors.source = "Select a source.";
    }

    if (needsPaymentFields && !form.paymentOption) {
      nextErrors.paymentOption = "Select a payment option.";
    }

    if (needsDeliveryFields && !form.deliveryType) {
      nextErrors.deliveryType = "Select a delivery type.";
    }

    if (!canCreateOrderWithoutMedicine && !validLines.length) {
      nextErrors.lines = "Add at least one medicine or manual item.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    const validLines = form.lines.filter(
      (line) => line.title && Number(line.quantity) > 0,
    );
    const basketValue = validLines.reduce((sum, line) => sum + getLineTotal(line), 0);
    const discountAmount =
      form.discountPercentage && Number(form.discountPercentage) > 0
        ? (basketValue * Number(form.discountPercentage)) / 100
        : 0;
    const marginAmount =
      form.marginValue && Number(form.marginValue) > 0
        ? form.marginType === "1"
          ? (basketValue * Number(form.marginValue)) / 100
          : Number(form.marginValue)
        : 0;
    const totalCost =
      basketValue +
      Number(form.deliveryCharge || 0) +
      marginAmount -
      discountAmount;

    const payload = {
      lead_id: form.subscriber && form.subscriber.id,
      source: form.source,
      opharma_reference: form.opharmaReference,
      mypharma_reference: form.mypharmaReference,
      flash_reference: form.flashReference,
      order_date: formatDateForApi(form.orderDate),
      order_status: form.orderType === "2" ? "13" : "1",
      order_sub_type: form.orderType,
      channel: "admin",
      sales_user_id: form.salesRep,
      order_note: form.orderNote,
      internal_note: form.internalNote,
      po_number: form.poNumber,
      po_values: form.poValue,
      tags: form.healthCondition
        ? form.healthCondition.split(",").map((tag) => tag.trim())
        : [],
      basketvalue: basketValue.toFixed(2),
      totalcost: totalCost.toFixed(2),
      total_cost: totalCost.toFixed(2),
      margin_type: form.marginType,
      margin: form.marginValue || 0,
      organization_discount: form.discountPercentage || 0,
      partial_sent: form.isPartialOrder,
      is_abusable: form.isAbusableOrder,
      is_claimable: form.isClaimableOrder ? "1" : "0",
      store_id: auth.store === null ? 1 : auth.store,
      medications: validLines.map((line) => ({
          id: line.id,
          title: line.title,
          batch_no: line.batchNo,
          barcode: line.barcode,
          item_code: line.itemCode,
          quantity: Number(line.quantity),
          unit_price: Number(line.unitPrice || 0),
          item_type: 1,
          margin_discount_sub_type: line.marginDiscountType || 2,
          margin_discount_price:
            String(line.marginDiscountType || "2") === "2"
              ? Number(line.marginDiscount || 0)
              : 0,
          margin_discount_percentage:
            String(line.marginDiscountType || "2") === "1"
              ? Number(line.marginDiscount || 0)
              : 0,
          claimable: line.claimable,
          is_recommended_product: line.upseller || false,
          item_ordered: line.packed || false,
          is_order_placed: line.packed || false,
          partial_sent: line.partialSent || false,
          is_printable: line.printable,
          item_description: line.note,
          batches: line.batches || [],
        })),
    };

    if (needsPaymentFields) {
      payload.paymentoption = form.paymentOption;
      payload.payment_due_date = formatDateForApi(form.paymentDueDate);
    }

    if (needsDeliveryFields) {
      payload.delivery_type = form.deliveryType;
      payload.delivery_charge = form.deliveryCharge || 0;
      payload.delivery_from_date = form.deliveryFromDate || "";
      payload.delivery_to_date = form.deliveryToDate || "";
      payload.is_courier_partner = form.isCourier ? 1 : 0;
      payload.rider_id = form.isCourier ? form.courierPartner : form.rider;
      payload.tracking_id = form.trackingId;
      payload.tracking_url = form.trackingUrl;
      payload.tracking_note = form.trackingNote;
      payload.address_id = form.addressId || 0;
      payload.address_line1 = form.addressLine1;
      payload.address_line2 = form.addressLine2;
      payload.city = form.city;
      payload.country_id = form.countryId || "1";
      payload.postal_code = form.postalCode;
      payload.billing_address_id = form.billingAddressId || form.addressId || 0;
    }

    if (form.isScheduleOrder && form.scheduleOrderDateTime) {
      payload.schedule_order_datetime = form.scheduleOrderDateTime;
    }

    return payload;
  };

  const createOrder = async () => {
    if (!validate()) {
      return;
    }

    setLoadingKey("create");
    setMessage(null);
    try {
      const payload = buildPayload();

      if (form.prescriptionFiles.length) {
        const uploadResponse = await uploadOrderPrescriptionFiles(
          auth.token,
          form.prescriptionFiles,
        );
        const prescriptionUrl = getUploadedPrescriptionUrl(uploadResponse);
        payload.prescription_file = prescriptionUrl;
        payload.prescription_file_url = prescriptionUrl;
      }

      const response = USE_ORDER_V2_CREATE
        ? await createMedicineOrderV2(auth.token, payload)
        : await createMedicineOrderClassic(auth.token, payload);

      if (response && response.success === false) {
        throw new Error(response.message || "Order create failed.");
      }

      localStorage.removeItem("medicineOrderBetaDraft");
      setForm(createInitialForm());
      setSubscriberQuery("");
      setSubscriberResults([]);
      setPoReviewLines([]);
      setPoImportSummary(null);
      setPoAppliedOnce(false);
      setPoRefillCreated(false);
      setMessage({
        type: "success",
        text: `Order created${
          response && response.data && response.data.id ? ` #${response.data.id}` : ""
        }.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text:
          (error && error.message) ||
          "Order create failed. Your data is still available on this page.",
      });
    } finally {
      setLoadingKey("");
    }
  };

  const poSelectedCount = poReviewLines.filter((line) => line.selected).length;
  const poShortageCount = poReviewLines.filter(
    (line) => line.selected && line.medication && getShortageQuantity(line) > 0,
  ).length;
  const poRefillCount = poReviewLines.filter(
    (line) =>
      line.selected &&
      line.medication &&
      getShortageQuantity(line) > 0 &&
      line.refillRequested &&
      Number(line.refillQuantity || getShortageQuantity(line)) > 0,
  ).length;
  const selectedPoReviewLines = poReviewLines.filter((line) => line.selected);
  const poReviewPoTotal = selectedPoReviewLines.reduce(
    (sum, line) => sum + getPoLineTotal(line),
    0,
  );
  const poReviewMedicationTotal = selectedPoReviewLines.reduce(
    (sum, line) => sum + getSystemLineTotal(line),
    0,
  );
  const poReviewTotalDifference = poReviewPoTotal - poReviewMedicationTotal;
  const poMissingPriceCount = selectedPoReviewLines.filter(
    (line) => getPoUnitPrice(line) === "",
  ).length;

  return (
    <div className="add-order-beta-page">
      <div className="aob-header">
        <div>
          <div className="aob-eyebrow">
            Medicine Order <span>Beta</span>
          </div>
          <h1>New Order</h1>
          <p>
            A lighter order builder that loads subscriber and medicine data only
            when you search.
          </p>
        </div>
        <div className="aob-header-actions">
          <Link
            className="aob-secondary-button"
            to="/admin/medicine-order-improved"
          >
            <i className="fa fa-arrow-left" /> Beta list
          </Link>
          <button
            className="aob-secondary-button"
            type="button"
            onClick={() => history.push("/admin/add-order-2")}
          >
            <i className="fa fa-external-link" /> Classic create
          </button>
        </div>
      </div>

      {message ? (
        <div className={`aob-message aob-message-${message.type}`}>
          {message.text}
        </div>
      ) : null}

      <div className="aob-layout">
        <main className="aob-main">
          <section className="aob-section aob-subscriber-first">
            <div className="aob-section-title">
              <i className="fa fa-user-circle" />
              <div>
                <h2>Subscriber</h2>
                <p>Start here. Type a mobile number or name and matching customers load automatically.</p>
              </div>
            </div>

            <div className="aob-search-row">
              <input
                value={subscriberQuery}
                onChange={(event) => {
                  setSubscriberQuery(event.target.value);
                  if (form.subscriber) {
                    updateField("subscriber", null);
                  }
                }}
                placeholder="Type subscriber mobile number or name"
              />
            </div>
            <span className="aob-auto-search-note">
              {loadingKey === "subscribers"
                ? "Searching subscribers..."
                : searchCacheLoading.subscribers
                  ? "Loading subscriber quick search in the background..."
                  : "Search is local first. New records are checked after a longer mobile/name search."}
            </span>
            {errors.subscriber ? (
              <div className="aob-error">{errors.subscriber}</div>
            ) : null}

            {form.subscriber ? (
              <div className="aob-selected-person">
                <i className="fa fa-check-circle" />
                <div>
                  <strong>{form.subscriber.name}</strong>
                  <span>
                    {form.subscriber.mobile || form.subscriber.email || "No contact"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    updateField("subscriber", null);
                    setSubscriberQuery("");
                  }}
                >
                  Change
                </button>
              </div>
            ) : null}

            {subscriberResults.length ? (
              <div className="aob-results">
                {subscriberResults.map((subscriber) => (
                  <button
                    key={subscriber.id}
                    type="button"
                    onClick={() => selectSubscriber(subscriber)}
                  >
                    <strong>{subscriber.name}</strong>
                    <span>
                      {subscriber.mobile || subscriber.email || "No contact"}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="aob-section">
            <div className="aob-section-title">
              <i className="fa fa-list-alt" />
              <div>
                <h2>Order Details</h2>
                <p>Keep the key fields visible first, then add items.</p>
              </div>
            </div>

            <div className="aob-type-grid">
              {ORDER_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={`aob-type-button ${
                    form.orderType === type.value ? "active" : ""
                  }`}
                  type="button"
                  onClick={() => handleOrderTypeChange(type.value)}
                >
                  <i className={`fa ${type.icon}`} />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>

            <div className="aob-grid">
              {/*
              <Field label="Order Date">
                <input
                  type="date"
                  value={form.orderDate}
                  onChange={(event) =>
                    updateField("orderDate", event.target.value)
                  }
                />
              </Field>
              */}
              <Field label="Source">
                <select
                  value={form.source}
                  onChange={(event) =>
                    updateField("source", event.target.value)
                  }
                >
                  {SOURCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.source ? <em>{errors.source}</em> : null}
              </Field>
              <Field label="Sales Rep">
                <input
                  value={form.salesRep}
                  onChange={(event) =>
                    updateField("salesRep", event.target.value)
                  }
                  placeholder="Name or ID"
                />
              </Field>
              <Field label="Health Condition">
                <input
                  value={form.healthCondition}
                  onChange={(event) =>
                    updateField("healthCondition", event.target.value)
                  }
                  placeholder="Diabetes, Cardiac..."
                />
              </Field>
              {isB2b ? (
                <Field label="PO Number">
                  <input
                    value={form.poNumber}
                    onChange={(event) =>
                      updateField("poNumber", event.target.value)
                    }
                    placeholder="Client PO number"
                  />
                </Field>
              ) : null}
            </div>
          </section>

          {isB2b ? (
            <section className="aob-section">
              <div className="aob-section-title">
                <i className="fa fa-magic" />
                <div>
                  <h2>PO Intake</h2>
                  <p>Open the focused PO intake modal when a B2B order needs document processing.</p>
                </div>
              </div>

              <div className="aob-po-launch">
                <div>
                  <strong>{form.poFiles.length ? form.poFiles[0].name : "No PO uploaded yet"}</strong>
                  <span>
                    {poReviewLines.length
                      ? `${poReviewLines.length} PO line(s) ready for review.`
                      : "Upload the PO and process it before applying medicines."}
                  </span>
                </div>
                <button
                  className="aob-dark-button"
                  type="button"
                  onClick={() => setPoIntakeOpen(true)}
                >
                  <i className="fa fa-magic" /> Open PO intake
                </button>
              </div>
            </section>
          ) : null}

          {!isB2b ? (
            <section className="aob-section">
              <div className="aob-section-title">
                <i className="fa fa-paperclip" />
                <div>
                  <h2>Documents</h2>
                  <p>Attach prescription and supporting documents before adding items.</p>
                </div>
              </div>

              <div className="aob-file-grid">
                <label className="aob-file-box">
                  <i className="fa fa-file-text" />
                  <strong>Prescription</strong>
                  <FileSummary files={form.prescriptionFiles} />
                  <input
                    multiple
                    type="file"
                    onChange={(event) =>
                      updateField(
                        "prescriptionFiles",
                        Array.from(event.target.files || []),
                      )
                    }
                  />
                </label>
                <label className="aob-file-box">
                  <i className="fa fa-upload" />
                  <strong>Supporting Document</strong>
                  <FileSummary files={form.documentFiles} />
                  <input
                    multiple
                    type="file"
                    onChange={(event) =>
                      updateField(
                        "documentFiles",
                        Array.from(event.target.files || []),
                      )
                    }
                  />
                </label>
              </div>
            </section>
          ) : (
            <section className="aob-section">
              <div className="aob-section-title">
                <i className="fa fa-paperclip" />
                <div>
                  <h2>Supporting Documents</h2>
                  <p>Attach any extra B2B documents apart from the PO.</p>
                </div>
              </div>

              <div className="aob-file-grid aob-file-grid-single">
                <label className="aob-file-box">
                  <i className="fa fa-upload" />
                  <strong>Supporting Document</strong>
                  <FileSummary files={form.documentFiles} />
                  <input
                    multiple
                    type="file"
                    onChange={(event) =>
                      updateField(
                        "documentFiles",
                        Array.from(event.target.files || []),
                      )
                    }
                  />
                </label>
              </div>
            </section>
          )}

          {needsPaymentFields || needsDeliveryFields ? (
          <section className="aob-section">
            <div className="aob-section-title">
              <i className={`fa ${needsDeliveryFields ? "fa-truck" : "fa-credit-card"}`} />
              <div>
                <h2>{needsDeliveryFields ? "Delivery & Payment" : "Payment"}</h2>
                <p>{needsDeliveryFields ? "Delivery, rider, and payment details." : "Pickup orders only need payment details here."}</p>
              </div>
            </div>

            <div className="aob-grid">
              {needsDeliveryFields ? (
              <Field label="Delivery Type">
                <select
                  value={form.deliveryType}
                  onChange={(event) =>
                    updateField("deliveryType", event.target.value)
                  }
                >
                  {DELIVERY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.deliveryType ? <em>{errors.deliveryType}</em> : null}
              </Field>
              ) : null}
              {needsPaymentFields ? (
              <Field label="Payment Option">
                <select
                  value={form.paymentOption}
                  onChange={(event) =>
                    updateField("paymentOption", event.target.value)
                  }
                >
                  {PAYMENT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.paymentOption ? <em>{errors.paymentOption}</em> : null}
              </Field>
              ) : null}
              {needsPaymentFields ? (
              <Field label="Payment Due Date">
                <input
                  type="date"
                  value={form.paymentDueDate}
                  onChange={(event) =>
                    updateField("paymentDueDate", event.target.value)
                  }
                />
              </Field>
              ) : null}
              {needsDeliveryFields ? (
              <Field label={form.isCourier ? "Courier Partner" : "Rider"}>
                <input
                  value={form.isCourier ? form.courierPartner : form.rider}
                  onChange={(event) =>
                    updateField(
                      form.isCourier ? "courierPartner" : "rider",
                      event.target.value,
                    )
                  }
                  placeholder={
                    form.isCourier ? "Courier partner ID" : "Rider ID"
                  }
                />
              </Field>
              ) : null}
            </div>

            {needsDeliveryFields ? (
            <label className="aob-check-row">
              <input
                checked={form.isCourier}
                type="checkbox"
                onChange={(event) =>
                  updateField("isCourier", event.target.checked)
                }
              />
              <span>Courier partner delivery</span>
            </label>
            ) : null}
          </section>
          ) : null}

          <section className="aob-section">
            <div className="aob-section-title">
              <i className="fa fa-medkit" />
              <div>
                <h2>Medicines</h2>
                <p>Type a batch number or medicine inside each item line.</p>
              </div>
            </div>

            <div className="aob-medicine-toolbar">
              {isB2b && poReviewLines.length ? (
                <button
                  className="aob-secondary-button"
                  type="button"
                  onClick={() => setPoReviewOpen(true)}
                >
                  <i className="fa fa-refresh" />{" "}
                  {poAppliedOnce ? "Review applied PO" : "Review PO again"}
                </button>
              ) : null}
              <button
                className="aob-light-button"
                type="button"
                onClick={addLine}
              >
                <i className="fa fa-plus" /> Manual item
              </button>
            </div>
            {errors.lines ? (
              <div className="aob-error">{errors.lines}</div>
            ) : null}

            <div className="aob-item-toolbar">
              <label className="aob-item-search">
                <i className="fa fa-search" />
                <input
                  value={itemSearch}
                  onChange={(event) => setItemSearch(event.target.value)}
                  placeholder="Find medicine, barcode, code, placement"
                />
                {itemSearch ? (
                  <button
                    type="button"
                    onClick={() => setItemSearch("")}
                    aria-label="Clear item search"
                  >
                    <i className="fa fa-times" />
                  </button>
                ) : null}
              </label>
              <div className="aob-item-filters">
                {[
                  ["all", "All"],
                  ["unpacked", "Unpacked"],
                  ["shortage", "Low stock"],
                  ["unmatched", "Needs medicine"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={itemFilter === value ? "is-active" : ""}
                    onClick={() => setItemFilter(value)}
                  >
                    {label}
                    <span>{itemFilterCounts[value]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="aob-lines">
              {form.lines.length > 0 && visibleItemRows.length === 0 ? (
                <div className="aob-empty-line">No items match this search.</div>
              ) : null}
              {visibleItemRows.map((row, visibleIndex) => {
                const { line, index, isAvailable, isLowStock } = row;

                return (
                  <div
                    className={`aob-line ${isLowStock ? "is-low-stock" : ""} ${
                      line.packed ? "is-packed-item" : ""
                    }`}
                    key={line.localId}
                  >
                    <div className="aob-line-top">
                      <div className="aob-line-main">
                        <div className="aob-line-name-row">
                          <span className="aob-line-number">{index + 1}</span>
                          <label className="aob-pack-toggle" title="Mark item packed">
                            <input
                              checked={line.packed || false}
                              type="checkbox"
                              onChange={(event) =>
                                updateLine(line.localId, "packed", event.target.checked)
                              }
                            />
                          </label>
                          <strong>{line.title || "New item"}</strong>
                        </div>
                        <div className="aob-line-edit-row">
                          <input
                            value={line.batchNo}
                            onChange={(event) =>
                              updateLine(line.localId, "batchNo", event.target.value)
                            }
                            placeholder="Batch No"
                          />
                          <div className="aob-line-medicine">
                            <input
                              className="aob-line-title"
                              ref={(node) => {
                                if (node) {
                                  lineMedicineInputRefs.current[line.localId] = node;
                                } else {
                                  delete lineMedicineInputRefs.current[line.localId];
                                }
                              }}
                              value={line.title}
                              onChange={(event) => {
                                setActiveMedicineLineId(line.localId);
                                updateLine(line.localId, "id", "");
                                updateLine(line.localId, "title", event.target.value);
                              }}
                              onFocus={() => setActiveMedicineLineId(line.localId)}
                              onKeyDown={(event) =>
                                handleLineMedicineKeyDown(event, line.localId)
                              }
                              placeholder="Medicine or item description"
                            />
                            {activeMedicineLineId === line.localId &&
                            line.title.trim().length >= 3 ? (
                              <div className="aob-inline-results">
                                {loadingKey === "medicines" ? (
                                  <span>Searching medicines...</span>
                                ) : null}
                                {searchCacheLoading.medicines &&
                                loadingKey !== "medicines" ? (
                                  <span>Loading medicine quick search...</span>
                                ) : null}
                                {lineMedicineResults.map((medicine, resultIndex) => (
                                  <button
                                    key={medicine.id}
                                    className={
                                      resultIndex === lineMedicineHighlightIndex
                                        ? "is-active"
                                        : ""
                                    }
                                    type="button"
                                    onMouseEnter={() =>
                                      setLineMedicineHighlightIndex(resultIndex)
                                    }
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() =>
                                      selectLineMedicine(line.localId, medicine)
                                    }
                                  >
                                    <strong>{medicine.title}</strong>
                                    <span>
                                      Rs. {formatMoney(medicine.price)} · Stock{" "}
                                      {toSafeNumber(medicine.availableQuantity, 0)}
                                      {medicine.itemCode ? ` · Code ${medicine.itemCode}` : ""}
                                    </span>
                                  </button>
                                ))}
                                {!lineMedicineResults.length &&
                                loadingKey !== "medicines" ? (
                                  <span>No matches yet. Keep typing or use this as a manual item.</span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="aob-line-meta">
                          <span>Item code: {line.itemCode || "-"}</span>
                          <span>Barcode: {line.barcode || "-"}</span>
                          <span>Placement: {line.location || "-"}</span>
                        </div>
                      </div>
                      <div className="aob-line-numbers">
                        <button
                          className="aob-line-delete"
                          title="Remove item"
                          type="button"
                          onClick={() => removeLine(line.localId)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                        <label>
                          <span>Qty</span>
                          <input
                            min="1"
                            type="number"
                            ref={(node) => {
                              if (node) {
                                lineQuantityInputRefs.current[line.localId] = node;
                              } else {
                                delete lineQuantityInputRefs.current[line.localId];
                              }
                            }}
                            value={line.quantity}
                            onChange={(event) =>
                              updateLine(line.localId, "quantity", event.target.value)
                            }
                            onKeyDown={(event) =>
                              handleLineQuantityKeyDown(event, line, visibleIndex)
                            }
                          />
                        </label>
                        <label>
                          <span>Unit</span>
                          <input
                            min="0"
                            step="0.01"
                            type="number"
                            value={line.unitPrice}
                            onChange={(event) =>
                              updateLine(line.localId, "unitPrice", event.target.value)
                            }
                          />
                        </label>
                        <strong>Rs. {formatMoney(getLineTotal(line))}</strong>
                      </div>
                    </div>
                    <div className="aob-line-options">
                      <label className={line.packed ? "is-packed" : ""}>
                        <input
                          checked={line.packed || false}
                          type="checkbox"
                          onChange={(event) =>
                            updateLine(line.localId, "packed", event.target.checked)
                          }
                        />
                        Packed
                      </label>
                      <label className={isAvailable ? "is-on" : ""}>
                        <input
                          checked={isAvailable}
                          type="checkbox"
                          onChange={(event) =>
                            updateLine(
                              line.localId,
                              "partialSent",
                              !event.target.checked,
                            )
                          }
                        />
                        Available
                      </label>
                      <label className={line.printable ? "is-on" : ""}>
                        <input
                          checked={line.printable}
                          type="checkbox"
                          onChange={(event) =>
                            updateLine(line.localId, "printable", event.target.checked)
                          }
                        />
                        Printable
                      </label>
                      <label className={line.claimable ? "is-on" : ""}>
                        <input
                          checked={line.claimable}
                          type="checkbox"
                          onChange={(event) =>
                            updateLine(line.localId, "claimable", event.target.checked)
                          }
                        />
                        Claimable
                      </label>
                      <label className={line.upseller ? "is-on" : ""}>
                        <input
                          checked={line.upseller || false}
                          type="checkbox"
                          onChange={(event) =>
                            updateLine(line.localId, "upseller", event.target.checked)
                          }
                        />
                        Upseller
                      </label>
                      <input
                        value={line.note}
                        onChange={(event) =>
                          updateLine(line.localId, "note", event.target.value)
                        }
                        placeholder="Item note"
                      />
                    </div>
                    <div className="aob-line-stock-panel">
                      <div className="aob-stock-label">Stock Availability</div>
                      <span>
                        <b>Warehouse inventory</b>
                        {toSafeNumber(line.storeQuantity, 0)}
                      </span>
                      <span>
                        <b>Pharmacy inventory</b>
                        {toSafeNumber(line.pharmacyQuantity, 0)}
                      </span>
                      <span>
                        <b>Medicine inventory</b>
                        {toSafeNumber(line.availableQuantity, 0)}
                      </span>
                      <span>
                        <b>Allocated</b>
                        {toSafeNumber(line.allocatedQuantity, 0)}
                      </span>
                      <span>
                        <b>System price</b>
                        Rs. {formatMoney(line.sellingPrice || line.unitPrice)}
                      </span>
                      {line.refillRequested ? (
                        <span className="aob-line-refill-chip">
                          <b>Refill</b>
                          {line.refillQuantity || "-"}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="aob-item-save-bar">
              <div className="aob-bottom-quick-add">
                <button type="button" className="aob-light-button" onClick={addLine}>
                  <i className="fa fa-plus" /> Add item
                </button>
                {isB2b ? (
                  <>
                    <button type="button" className="aob-light-button" onClick={() => addLineRows(2)}>
                      Add 2 rows
                    </button>
                    <button type="button" className="aob-light-button" onClick={() => addLineRows(3)}>
                      Add 3 rows
                    </button>
                    <button type="button" className="aob-light-button" onClick={() => addLineRows(5)}>
                      Add 5 rows
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </section>

          <section className="aob-section">
            <div className="aob-section-title">
              <i className={needsDeliveryFields ? "fa fa-map-marker" : "fa fa-sticky-note"} />
              <div>
                <h2>{needsDeliveryFields ? "Address & Notes" : "Notes"}</h2>
                <p>{needsDeliveryFields ? "Capture only what is needed for this order." : "Internal and order notes for this request."}</p>
              </div>
            </div>

            <div className="aob-grid">
              {needsDeliveryFields ? (
                <>
              <Field label="Address Line 1" full>
                <input
                  value={form.addressLine1}
                  onChange={(event) =>
                    updateField("addressLine1", event.target.value)
                  }
                  placeholder="Street, building, area"
                />
              </Field>
              <Field label="Address Line 2">
                <input
                  value={form.addressLine2}
                  onChange={(event) =>
                    updateField("addressLine2", event.target.value)
                  }
                  placeholder="Apartment or landmark"
                />
              </Field>
              <Field label="City / District">
                <input
                  value={form.city}
                  onChange={(event) => updateField("city", event.target.value)}
                  placeholder="City"
                />
              </Field>
              <Field label="Postal Code">
                <input
                  value={form.postalCode}
                  onChange={(event) =>
                    updateField("postalCode", event.target.value)
                  }
                  placeholder="Postal code"
                />
              </Field>
                </>
              ) : null}
              <Field label="Order Note" full>
                <textarea
                  value={form.orderNote}
                  onChange={(event) =>
                    updateField("orderNote", event.target.value)
                  }
                  placeholder="Visible order note"
                />
              </Field>
              <Field label="Internal Note" full>
                <textarea
                  value={form.internalNote}
                  onChange={(event) =>
                    updateField("internalNote", event.target.value)
                  }
                  placeholder="Team note"
                />
              </Field>
            </div>
          </section>

        </main>

        <aside className="aob-summary">
          <div className="aob-summary-card">
            <div className="aob-summary-top">
              <span>Beta Summary</span>
              <strong>Rs. {formatMoney(total)}</strong>
            </div>
            <dl>
              <div>
                <dt>Subscriber</dt>
                <dd>
                  {form.subscriber ? form.subscriber.name : "Not selected"}
                </dd>
              </div>
              <div>
                <dt>Items</dt>
                <dd>
                  {form.lines.filter((line) => line.title).length} line
                  {form.lines.filter((line) => line.title).length === 1
                    ? ""
                    : "s"}
                </dd>
              </div>
              {needsPaymentFields ? (
                <div>
                  <dt>Payment</dt>
                  <dd>{form.paymentOption}</dd>
                </div>
              ) : null}
              {needsDeliveryFields ? (
                <div>
                  <dt>Delivery</dt>
                  <dd>
                    {
                      DELIVERY_OPTIONS.find(
                        (option) => option.value === form.deliveryType,
                      ).label
                    }
                  </dd>
                </div>
              ) : null}
            </dl>
            <button
              className="aob-primary-button"
              disabled={loadingKey === "create"}
              type="button"
              onClick={createOrder}
            >
              <i className="fa fa-check" />
              {form.orderType === "2" ? "Save Draft" : "Create Order"}
            </button>
            <button
              className="aob-plain-button"
              type="button"
              onClick={() => history.push("/admin/add-order-2")}
            >
              Continue in classic create
            </button>
            <p>
              Live creation stays on V2 only in this Beta page. Existing create
              order is untouched.
            </p>
          </div>
        </aside>
      </div>
      {poIntakeOpen ? (
        <div className="aob-modal-backdrop">
          <div className="aob-po-intake-modal">
            <div className="aob-po-modal-header">
              <div>
                <h2>PO Intake</h2>
                <p>Upload the client PO, read all item rows, then review matched medicines.</p>
              </div>
              <button
                className="aob-icon-button"
                title="Close"
                type="button"
                onClick={() => setPoIntakeOpen(false)}
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="aob-po-intake-body">
              <label className="aob-file-box">
                <i className="fa fa-file-pdf-o" />
                <strong>Purchase Order</strong>
                <FileSummary files={form.poFiles} />
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv,.docx"
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);
                    updateField("poFiles", files);
                    setPoMessage(
                      files.length
                        ? { type: "info", text: `${files[0].name} is uploading and processing.` }
                        : null,
                    );
                    if (files.length) {
                      processPoDocument(files);
                    }
                  }}
                />
              </label>
              <div className="aob-po-actions">
                {poReviewLines.length ? (
                  <button
                    className="aob-light-button"
                    type="button"
                    onClick={() => {
                      setPoIntakeOpen(false);
                      setPoReviewOpen(true);
                    }}
                  >
                    <i className="fa fa-refresh" />{" "}
                    {poAppliedOnce ? "Review applied PO" : "Review PO again"}
                  </button>
                ) : null}
                <span>
                  {loadingKey === "po"
                    ? "Processing the uploaded PO now."
                    : "Upload a PO to process it automatically."}{" "}
                  The PO is read first, then matched against B2B learning, previous
                  orders, medication history, and store pricing suggestions.
                </span>
                {poMessage ? (
                  <div className={`aob-message aob-message-${poMessage.type} aob-po-message`}>
                    {poMessage.text}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="aob-po-modal-footer">
              <button
                className="aob-secondary-button"
                type="button"
                onClick={() => setPoIntakeOpen(false)}
              >
                Continue without PO
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {poReviewOpen ? (
        <div className="aob-modal-backdrop">
          <div className="aob-po-modal">
            <div className="aob-po-modal-header">
              <div>
                <h2>Review PO Lines</h2>
                <p>
                  {poImportSummary
                    ? `${poImportSummary.count} line(s) from ${poImportSummary.fileName} via ${poImportSummary.method}.`
                    : "Review matched PO lines before applying."}
                </p>
              </div>
              <div className="aob-po-modal-summary">
                <span>{poSelectedCount} selected</span>
                <span>{poShortageCount} shortage</span>
                <span>{poRefillCount} refill</span>
              </div>
              <button
                className="aob-icon-button"
                title="Close"
                type="button"
                onClick={() => setPoReviewOpen(false)}
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="aob-po-table-wrap">
              <div className="aob-po-modal-toolbar">
                <button
                  className="aob-light-button"
                  type="button"
                  onClick={addPoReviewLine}
                >
                  <i className="fa fa-plus" /> Add item
                </button>
                <button
                  className="aob-secondary-button"
                  disabled={loadingKey === "po-recheck"}
                  type="button"
                  onClick={recheckPoReviewLines}
                >
                  <i className="fa fa-refresh" />{" "}
                  {loadingKey === "po-recheck" ? "Rechecking" : "Recheck matches"}
                </button>
                <span>Matched lines will fill the order. Shortages can create one refill request.</span>
              </div>
              <table className="aob-po-table">
                <thead>
                  <tr>
                    <th>Use</th>
                    <th>PO Requested Item</th>
                    <th>Flash Medicine</th>
                    <th>Qty</th>
                    <th>Stock</th>
                    <th>Refill</th>
                    <th>PO Price</th>
                    <th>System Price</th>
                  </tr>
                </thead>
                <tbody>
                  {poReviewLines.map((line, index) => {
                    const hasShortage = line.medication && getShortageQuantity(line) > 0;
                    return (
                    <tr
                      className={[
                        line.selected ? "aob-po-row-selected" : "aob-po-row-muted",
                        hasShortage ? "aob-po-row-low-stock" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      key={`${line.requestedName}-${index}`}
                    >
                      <td>
                        <input
                          checked={line.selected}
                          type="checkbox"
                          onChange={(event) =>
                            updatePoReviewLine(index, "selected", event.target.checked)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={line.requestedName}
                          onChange={(event) =>
                            updatePoReviewLine(index, "requestedName", event.target.value)
                          }
                          placeholder="Requested item"
                        />
                        {line.requestedUnit ? <span>{line.requestedUnit}</span> : null}
                      </td>
                      <td>
                        {line.medication ? (
                          <>
                            <strong>{getMedicationTitle(line.medication)}</strong>
                            <span>
                              Rs. {formatMoney(getMedicationPrice(line.medication))}
                              {getMedicationItemCode(line.medication)
                                ? ` | Code ${getMedicationItemCode(line.medication)}`
                                : ""}
                            </span>
                          </>
                        ) : (
                          <span>No automatic match</span>
                        )}
                        <div className="aob-po-medicine-search">
                          <button
                            className="aob-plain-button"
                            type="button"
                            onClick={() => openPoMedicineSearch(index)}
                          >
                            {line.medication ? "Change medicine" : "Search medicine"}
                          </button>
                          {poMedicineSearchIndex === index ? (
                            <div className="aob-po-search-panel">
                              <input
                                value={poMedicineSearchQuery}
                                onChange={(event) =>
                                  setPoMedicineSearchQuery(event.target.value)
                                }
                                placeholder="Search medicine name or item code"
                              />
                              <div className="aob-po-search-results">
                                {poMedicineSearchLoading ? (
                                  <span>Searching medicines...</span>
                                ) : null}
                                {!poMedicineSearchLoading &&
                                poMedicineSearchQuery.trim().length >= 3 &&
                                !poMedicineSearchResults.length ? (
                                  <span>No matches found.</span>
                                ) : null}
                                {poMedicineSearchResults.map((medicine) => (
                                  <button
                                    key={medicine.id}
                                    type="button"
                                    onClick={() => selectPoMedication(index, medicine)}
                                  >
                                    <strong>{getMedicationTitle(medicine)}</strong>
                                    <span>
                                      Rs. {formatMoney(getMedicationPrice(medicine))}
                                      {getMedicationItemCode(medicine)
                                        ? ` | Code ${getMedicationItemCode(medicine)}`
                                        : ""}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <input
                          min="1"
                          type="number"
                          value={line.quantity}
                          onChange={(event) =>
                            updatePoReviewLine(index, "quantity", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        {line.medication ? (
                          <>
                            <strong>{line.availableQuantity || 0}</strong>
                            {getShortageQuantity(line) > 0 ? (
                              <span className="aob-stock-short">
                                Short {getShortageQuantity(line)}
                              </span>
                            ) : (
                              <span className="aob-stock-ok">Enough stock</span>
                            )}
                          </>
                        ) : (
                          <span>Select medicine</span>
                        )}
                      </td>
                      <td>
                        {!line.medication ? (
                          <span>Match first</span>
                        ) : hasShortage ? (
                          <div className="aob-refill-cell">
                            <label>
                              <input
                                checked={line.refillRequested}
                                type="checkbox"
                                onChange={(event) =>
                                  updatePoReviewLine(index, "refillRequested", event.target.checked)
                                }
                              />
                              Request
                            </label>
                            <input
                              min="1"
                              placeholder="Qty"
                              type="number"
                              value={line.refillQuantity}
                              onChange={(event) =>
                                updatePoReviewLine(index, "refillQuantity", event.target.value)
                              }
                            />
                            <input
                              value={line.refillRemark}
                              onChange={(event) =>
                                updatePoReviewLine(index, "refillRemark", event.target.value)
                              }
                              placeholder="Remark"
                            />
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <input
                          min="0"
                          placeholder="Not fetched"
                          step="0.01"
                          type="number"
                          value={line.requestedPrice}
                          onChange={(event) =>
                            updatePoReviewLine(index, "requestedPrice", event.target.value)
                          }
                        />
                        {line.requestedTotal ? (
                          <span>Total Rs. {formatMoney(line.requestedTotal)}</span>
                        ) : null}
                        {getPoUnitPrice(line) === "" ? (
                          <span className="aob-po-price-missing">PO price missing</span>
                        ) : null}
                      </td>
                      <td>
                        {line.medication ? (
                          <>
                            <strong>
                              Rs. {formatMoney(getMedicationPrice(line.medication))}
                            </strong>
                            {line.requestedPrice ? (
                              <span>
                                Diff Rs.{" "}
                                {formatMoney(
                                  Number(line.requestedPrice || 0) -
                                    Number(getMedicationPrice(line.medication) || 0),
                                )}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
            <div className="aob-po-modal-footer">
              <div className="aob-po-total-strip">
                <span>
                  <b>PO total</b>
                  Rs. {formatMoney(poReviewPoTotal)}
                </span>
                <span>
                  <b>Medication total</b>
                  Rs. {formatMoney(poReviewMedicationTotal)}
                </span>
                <span
                  className={
                    Math.abs(poReviewTotalDifference) > 0.01 || poMissingPriceCount
                      ? "aob-po-total-diff aob-po-total-diff-alert"
                      : "aob-po-total-diff"
                  }
                >
                  <b>Difference</b>
                  Rs. {formatMoney(poReviewTotalDifference)}
                </span>
                {poMissingPriceCount ? (
                  <em>{poMissingPriceCount} selected line(s) missing PO price</em>
                ) : null}
              </div>
              <button
                className="aob-secondary-button"
                type="button"
                onClick={() => setPoReviewOpen(false)}
              >
                Cancel
              </button>
              <button
                className="aob-primary-button"
                disabled={loadingKey === "po-apply"}
                type="button"
                onClick={applyPoReviewLines}
              >
                <i className="fa fa-check" />{" "}
                {loadingKey === "po-apply" ? "Applying lines..." : "Apply selected lines"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default withRouter(AddOrderBeta);
